import { ContentAnchor } from '@api/selections';
import { ContentLink, ImageConfig, LoadingStatus } from '@api/types';
import { LoadingSkeleton } from '@components';
import { Box, styled } from '@mui/material';
import { ImageResizerParams, getEpiImageResizerUrl } from '@utils/images.utils';
import classnames from 'classnames';

type ImgProps = {
  height?: string;
  anchor?: ContentAnchor;
};

const Img = styled('img')<ImgProps>`
  // Fill - cover the entire area
  object-fit: contain;
  object-position: center;
  &.fill {
    object-fit: cover;
    object-position: ${(props) => props.anchor};
    width: 100%;
    ${(props) => (props.height ? `height: ${props.height}` : ``)};
  }

  &.fit {
    object-fit: scale-down;
    width: 100%;
    height: 100%;
  }
  &.contain {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  &.scale-down {
    object-fit: scale-down;
    width: 100%;
    height: 100%;
  }
`;

export type ImageProps = LoadingStatus &
  ImageConfig & {
    image?: ContentLink | string;
    alt?: string;
    title?: string;
    width?: string;
    height?: string;
    className?: string;
    addWrapper?: boolean;
    resizerParams?: ImageResizerParams;
    imgClassName?: string;
  };

/*
 * Wrap and display CMS or external image
 */
export const Image = ({
  image,
  title,
  alt,
  className,
  width,
  height,
  isLoading,
  scalingMode,
  anchor,
  addWrapper,
  resizerParams,
  imgClassName,
}: ImageProps) => {
  // No image provided
  if (!image) {
    return null;
  }
  const imageUrl = getEpiImageResizerUrl(image, resizerParams);

  // Get main class name based on config
  const mainClassName = classnames('epi-image-wrapper', className);

  const imageClassName = classnames('epi-image', scalingMode, anchor, imgClassName);

  if (!imageUrl) {
    return null;
  }

  const content = (
    <>
      {isLoading && (
        <LoadingSkeleton width={width} height={height} className='epi-image-skeleton' />
      )}
      {!isLoading && (
        <Img
          src={imageUrl}
          loading='lazy'
          alt={alt}
          title={title}
          className={imageClassName}
          width={width}
          height={height}
          anchor={anchor}
        />
      )}
    </>
  );

  return addWrapper ? (
    <Box className={mainClassName} sx={{ width: '100%', height }}>
      {content}
    </Box>
  ) : (
    content
  );
};
